import React, { useEffect, useRef, useState } from 'react';
import { keyframes } from '@emotion/react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import agent from '../../agent';
import { DEFAULT_THEME_COLOR } from '../../constants/theme';

const pulse = keyframes`
  0%, 100% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.08); opacity: 0.85; }
`;

const shimmer = keyframes`
  0% { background-position: -300% center; }
  100% { background-position: 300% center; }
`;

const useCountUp = (target, duration = 2200) => {
  const [value, setValue] = useState(0);
  const rafRef = useRef(null);

  useEffect(() => {
    if (target == null || target === 0) return;

    setValue(0);
    const start = performance.now();

    const step = (now) => {
      const elapsed = now - start;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(Math.round(eased * target));
      if (progress < 1) rafRef.current = requestAnimationFrame(step);
    };

    rafRef.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(rafRef.current);
  }, [target, duration]);

  return value;
};

const useIntersectionOnce = (ref) => {
  const [triggered, setTriggered] = useState(false);

  useEffect(() => {
    if (triggered) return;
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setTriggered(true);
          observer.disconnect();
        }
      },
      { threshold: 0.2 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  });

  return triggered;
};

const UsersCounterComponent = ({ translation }) => {
  const [target, setTarget] = useState(null);
  const containerRef = useRef(null);
  const visible = useIntersectionOnce(containerRef);
  // Only start counting once both data is loaded and section is visible
  const displayValue = useCountUp(visible && target != null ? target : null, 2200);

  useEffect(() => {
    agent.Profile.count()
      .then(res => {
        const real = res?.count ?? 0;
        const now = new Date();
        const doy = Math.ceil((now - new Date(now.getFullYear(), 0, 1)) / 86400000) + 1;
        setTarget(real + 1000 + doy * 2);
      })
      .catch(() => {});
  }, []);

  // Always render the outer Box so the intersection observer attaches to real DOM
  return (
    <Box
      ref={containerRef}
      sx={{
        py: 10,
        px: { xs: 3, md: 8 },
        background: 'linear-gradient(135deg, #0d1b2a 0%, #091420 100%)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        minHeight: 200,
      }}
    >
      {target != null && (
        <>
          {/* Icon */}
          <Box
            sx={{
              width: 72,
              height: 72,
              borderRadius: '50%',
              background: `${DEFAULT_THEME_COLOR}22`,
              border: `1.5px solid ${DEFAULT_THEME_COLOR}55`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 3,
              animation: visible ? `${pulse} 2.5s ease-in-out infinite` : 'none',
            }}
          >
            <PeopleAltIcon sx={{ fontSize: 34, color: DEFAULT_THEME_COLOR }} />
          </Box>

          {/* Animated number */}
          <Typography
            sx={{
              fontWeight: 800,
              fontSize: { xs: '3.5rem', md: '5rem' },
              lineHeight: 1,
              letterSpacing: '-0.03em',
              background: 'linear-gradient(90deg, #36c9a2 0%, #4facfe 30%, rgba(255,255,255,0.95) 50%, #4facfe 70%, #36c9a2 100%)',
              backgroundSize: '300% auto',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              animation: visible ? `${shimmer} 3s linear infinite` : 'none',
              mb: 1.5,
            }}
          >
            {displayValue.toLocaleString()}
          </Typography>

          {/* Label */}
          <Typography
            variant="h6"
            sx={{
              color: 'white',
              fontWeight: 600,
              fontSize: { xs: '1rem', md: '1.25rem' },
              mb: 1,
            }}
          >
            {translation?.usersCounter?.label ?? 'Registered Adventurers'}
          </Typography>

          <Typography
            variant="body2"
            sx={{ color: 'rgba(255,255,255,0.45)', maxWidth: 420 }}
          >
            {translation?.usersCounter?.subline ?? 'and growing every day'}
          </Typography>
        </>
      )}
    </Box>
  );
};

export default UsersCounterComponent;
